import type { Area } from 'react-easy-crop';

interface CropToSizeParams {
  imageSrc: string;
  cropArea: Area;
  targetWidth: number;
  targetHeight: number;
  mimeType?: string;
  quality?: number;
}

export interface CropResult {
  dataUrl: string;
  width: number;
  height: number;
  mimeType: string;
}

function createImageElement(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = source;
  });
}

export async function cropImageToSize({
  imageSrc,
  cropArea,
  targetWidth,
  targetHeight,
  mimeType = 'image/webp',
  quality = 0.92
}: CropToSizeParams): Promise<CropResult> {
  if (typeof document === 'undefined') {
    throw new Error('Cropping is only available in the browser environment.');
  }
  const image = await createImageElement(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(targetWidth));
  canvas.height = Math.max(1, Math.round(targetHeight));
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to obtain canvas context for cropping.');
  }

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  const dataUrl = canvas.toDataURL(mimeType, quality);
  return {
    dataUrl,
    width: canvas.width,
    height: canvas.height,
    mimeType
  };
}
